import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { extractError } from "../lib/errors";
import { motion } from "framer-motion";
import {
  FileText, Sparkles, Loader2, Download, Copy, Check,
  BookOpen, Shield, ClipboardList, Users, FileSignature, Library, Brain
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS as ALL_FRAMEWORKS } from "../constants/frameworks";

const FRAMEWORK_LABELS = ALL_FRAMEWORKS.map((f) => ({ id: f.id, name: f.label }));

const DOC_TYPE_KEYS = {
  policy: "pol.doctype.policy",
  procedure: "pol.doctype.procedure",
  form: "pol.doctype.form",
  job_description: "pol.doctype.job_description",
  guideline: "pol.doctype.guideline",
  checklist: "pol.doctype.checklist",
};

const DOC_TYPES = [
  { id: "policy", icon: Shield },
  { id: "procedure", icon: ClipboardList },
  { id: "form", icon: FileSignature },
  { id: "job_description", icon: Users },
  { id: "guideline", icon: BookOpen },
  { id: "checklist", icon: FileText }
];

const PolicyGenerator = () => {
  const { token } = useAuth();
  const { activeFrameworks } = useOrg();
  const FRAMEWORKS = FRAMEWORK_LABELS.filter((f) => activeFrameworks.includes(f.id));
  const { t, locale } = useI18n();
  const confirm = useConfirm();
  const navigate = useNavigate();

  // AI tab state
  const [selectedFw, setSelectedFw] = useState("iso27001");
  const [controls, setControls] = useState([]);
  const [selectedControl, setSelectedControl] = useState("");
  const [aiDocType, setAiDocType] = useState("policy");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Templates tab state
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(true);
  const [filterFw, setFilterFw] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [fillOpen, setFillOpen] = useState(false);
  const [instantiating, setInstantiating] = useState(false);
  const [instForm, setInstForm] = useState({
    org_name: "", org_address: "", version: "1.0",
    effective_date: new Date().toISOString().slice(0, 10),
    classification: "Internal",
    approver_name: "", approver_role: "CEO",
    reviewer_name: "", reviewer_role: "CISO",
    next_review_date: "", extra_context: ""
  });

  const fetchControls = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/controls?framework=${selectedFw}`, { headers: { Authorization: `Bearer ${token}` } });
      setControls(res.data || []);
    } catch { /* ignore */ }
  }, [selectedFw, token]);

  const fetchTemplates = useCallback(async () => {
    setTplLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterFw !== "all") params.append("framework", filterFw);
      if (filterType !== "all") params.append("doc_type", filterType);
      const res = await axios.get(`${API}/doc-templates?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(res.data || []);
    } catch (e) { console.error(e); }
    finally { setTplLoading(false); }
  }, [filterFw, filterType, token]);

  useEffect(() => { fetchControls(); }, [fetchControls]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Auto-correct selectedFw if the org's active frameworks no longer include it
  useEffect(() => {
    if (FRAMEWORKS.length > 0 && !FRAMEWORKS.find((f) => f.id === selectedFw)) {
      setSelectedFw(FRAMEWORKS[0].id);
    }
  }, [FRAMEWORKS, selectedFw]);

  const generateAI = async () => {
    if (!selectedControl) return;
    setGenerating(true);
    try {
      // LLM policy generation takes 45-60 s for a full document.
      // Override the global 30 s default only for this call so it doesn't
      // silently abort before the backend responds.
      const res = await axios.post(
        `${API}/policy-generator/generate?control_number=${selectedControl}&framework=${selectedFw}&doc_type=${aiDocType}&locale=${locale}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setResult(res.data);
      setEditedContent(res.data.content);
      // Make the AI result also editable using a synthetic "template" for save tracking
      setSelectedTpl({
        template_id: `ai_${selectedControl}`,
        title: res.data.control_title || selectedControl,
        doc_type: aiDocType,
        framework: selectedFw
      });
      setPreviewMode(true);
      toast.success(t("pol.doc_generated"));
    } catch (e) {
      // Always surface the error — previously this catch was silent on timeout
      toast.error(extractError(e, t("pol.generate_error_fallback")));
    } finally {
      // Guaranteed execution: re-enables the button even on timeout/error
      setGenerating(false);
    }
  };

  const openTemplate = (tpl) => { setSelectedTpl(tpl); setFillOpen(true); };

  const [previewMode, setPreviewMode] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [savingDoc, setSavingDoc] = useState(false);

  const submitInstantiate = async () => {
    if (!instForm.approver_name || !instForm.reviewer_name) {
      toast.error(t("pol.approver_reviewer_required"));
      return;
    }
    setInstantiating(true);
    try {
      // Preview only - do NOT save yet
      const res = await axios.post(
        `${API}/doc-templates/${selectedTpl.template_id}/instantiate?locale=${locale}`,
        { ...instForm, save_as_document: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult({ control_number: "", control_title: selectedTpl.title, framework: selectedTpl.framework, content: res.data.content });
      setEditedContent(res.data.content);
      setPreviewMode(true);
      setFillOpen(false);
      toast.success(t("pol.doc_generated"));
    } catch (e) {
      toast.error(extractError(e, "Failed"));
    } finally { setInstantiating(false); }
  };

  const saveEditedDoc = async () => {
    if (!selectedTpl || !editedContent) return;
    setSavingDoc(true);
    try {
      const title = `${selectedTpl.title_es || selectedTpl.title} — ${instForm.org_name || "Org"}`;
      const payload = {
        title,
        document_type: selectedTpl.doc_type,
        framework: selectedTpl.framework,
        content: editedContent,
        folder: selectedTpl.framework
      };
      const res = await axios.post(`${API}/documents`, payload, { headers: { Authorization: `Bearer ${token}` } });
      const shortId = res.data.document_id.slice(0, 12);
      toast.success(
        `${t("pol.saved_to_docs")} (ID: ${shortId}...)`,
        {
          action: {
            label: t("pol.view"),
            onClick: () => navigate(`/documents?highlight=${res.data.document_id}`),
          },
          duration: 8000,
        }
      );
      setPreviewMode(false);
      setResult(null);
      setSelectedTpl(null);
    } catch (e) {
      toast.error(extractError(e, "Save failed"));
    } finally { setSavingDoc(false); }
  };

  const discardEdit = async () => {
    if (await confirm({ title: t("pol.discard_title"), description: t("pol.discard_confirm"), destructive: true })) {
      setPreviewMode(false);
      setResult(null);
      setSelectedTpl(null);
      setEditedContent("");
    }
  };

  const copyContent = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(t("copied"));
  };

  const downloadContent = async () => {
    if (!result) return;
    try {
      const res = await axios.post(
        `${API}/policy-generator/export-docx`,
        {
          content_markdown: editedContent || result.content,
          title: result.control_title || selectedTpl?.title || "Document",
          framework: result.framework || selectedTpl?.framework,
          control_number: result.control_number || "",
          doc_type: selectedTpl?.doc_type || aiDocType,
          locale,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          timeout: 60000,
        }
      );
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      const fname = (result.control_title || "document")
        .replace(/[^a-z0-9]/gi, "_")
        .slice(0, 40);
      link.download = `${fname}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e, t("pol.export_failed")));
    }
  };

  const docTypeName = (id) => DOC_TYPE_KEYS[id] ? t(DOC_TYPE_KEYS[id]) : id;
  const docTypeIcon = (id) => {
    const dt = DOC_TYPES.find(d => d.id === id);
    return dt ? dt.icon : FileText;
  };

  return (
    <div className="p-6 space-y-6" data-testid="policy-generator-page">
      <PageHeader
        title={t("pol.title")}
        description={t("pol.subtitle")}
      />

      <Tabs defaultValue="templates">
        <TabsList>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <Library className="w-4 h-4 mr-2" />
            {t("pol.tab_templates")}
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">
            <Brain className="w-4 h-4 mr-2" />
            {t("pol.tab_ai")}
          </TabsTrigger>
        </TabsList>

        {/* ================= TEMPLATES TAB ================= */}
        <TabsContent value="templates" className="mt-4 space-y-4">
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="pt-4 flex gap-3 items-start">
              <Library className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">
                  {t("pol.template_library_title")}
                </p>
                <p className="text-muted-foreground">
                  {t("pol.template_library_desc")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3">
            <Select value={filterFw} onValueChange={setFilterFw}>
              <SelectTrigger className="w-full md:w-52" data-testid="filter-framework">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pol.all_frameworks")}</SelectItem>
                {FRAMEWORKS.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-52" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("pol.all_types")}</SelectItem>
                {DOC_TYPES.filter(d => ["policy", "procedure", "form", "job_description"].includes(d.id)).map(d =>
                  <SelectItem key={d.id} value={d.id}>{docTypeName(d.id)}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <div className="text-sm text-muted-foreground self-center">
              {templates.length} {t("pol.templates_available")}
            </div>
          </div>

          {/* Template Grid */}
          {tplLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : templates.length === 0 ? (
            <Card className="border-dashed border-2"><CardContent className="py-12 text-center text-muted-foreground">
              {t("pol.no_templates")}
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(tpl => {
                const Icon = docTypeIcon(tpl.doc_type);
                return (
                  <motion.div key={tpl.template_id} whileHover={{ scale: 1.02 }}>
                    <Card className="border-border h-full cursor-pointer hover:border-primary/50 transition" onClick={() => openTemplate(tpl)} data-testid={`tpl-${tpl.template_id}`}>
                      <CardContent className="pt-5 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-sm leading-snug">
                              {tpl.title_es || tpl.title}
                            </h3>
                            <div className="flex items-center gap-1 mt-1 flex-wrap">
                              <Badge variant="outline" className="text-[10px]">{FRAMEWORK_LABELS.find(f => f.id === tpl.framework)?.name || tpl.framework}</Badge>
                              <Badge variant="secondary" className="text-[10px]">{docTypeName(tpl.doc_type)}</Badge>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {tpl.description_es || tpl.description}
                        </p>
                        {tpl.control_refs?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {tpl.control_refs.slice(0, 3).map((c, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] font-mono">{c}</Badge>
                            ))}
                          </div>
                        )}
                        <Button size="sm" className="w-full" data-testid={`use-${tpl.template_id}`}>
                          <FileSignature className="w-4 h-4 mr-2" />
                          {t("pol.use_template")}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ================= AI TAB (existing behavior) ================= */}
        <TabsContent value="ai" className="mt-4 space-y-4">
          <Card className="border-border">
            <CardHeader><CardTitle className="text-lg">{t("pol.generate_ai_title")}</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>{t("gap.framework_select")}</Label>
                <Select value={selectedFw} onValueChange={setSelectedFw}>
                  <SelectTrigger data-testid="ai-framework"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FRAMEWORKS.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("pol.control_label")}</Label>
                <Select value={selectedControl} onValueChange={setSelectedControl}>
                  <SelectTrigger data-testid="ai-control"><SelectValue placeholder={t("pol.choose_control")} /></SelectTrigger>
                  <SelectContent>
                    {controls.slice(0, 100).map(c => (
                      <SelectItem key={c.control_number} value={c.control_number}>{c.control_number} — {c.title?.slice(0, 40)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("type")}</Label>
                <Select value={aiDocType} onValueChange={setAiDocType}>
                  <SelectTrigger data-testid="ai-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.filter(d => ["policy", "procedure", "guideline", "checklist"].includes(d.id)).map(d =>
                      <SelectItem key={d.id} value={d.id}>{docTypeName(d.id)}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Button onClick={generateAI} disabled={!selectedControl || generating} className="w-full" data-testid="ai-generate-btn">
                  {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("generating")}</>
                    : <><Sparkles className="w-4 h-4 mr-2" />{t("pol.generate_with_ai")}</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Result Panel (shared) */}
      {result && previewMode ? (
        <Card className="border-primary/50 border-2" data-testid="edit-preview">
          <CardHeader className="flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="default" className="text-[10px]">
                  {t("pol.edit_mode")}
                </Badge>
                <CardTitle className="text-lg">{result.control_title}</CardTitle>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {FRAMEWORK_LABELS.find(f => f.id === result.framework)?.name} · {t("pol.edit_before_save")}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={copyContent} data-testid="copy-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? t("copied") : t("copy")}
              </Button>
              <Button size="sm" variant="outline" onClick={downloadContent} data-testid="download-btn">
                <Download className="w-4 h-4 mr-2" />{t("download")}
              </Button>
              <Button size="sm" variant="ghost" onClick={discardEdit} data-testid="discard-btn">
                {t("pol.discard")}
              </Button>
              <Button size="sm" onClick={saveEditedDoc} disabled={savingDoc} data-testid="save-doc-btn">
                {savingDoc
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("pol.saving")}</>
                  : <><FileSignature className="w-4 h-4 mr-2" />{t("pol.save_to_docs")}</>}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={editedContent}
              onChange={(e) => { setEditedContent(e.target.value); setResult({ ...result, content: e.target.value }); }}
              rows={24}
              className="font-mono text-sm resize-y"
              data-testid="edit-textarea"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {editedContent.length} {t("pol.chars_hint")}
            </p>
          </CardContent>
        </Card>
      ) : result ? (
        <Card className="border-primary/50 border-2">
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">{result.control_title}</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {FRAMEWORK_LABELS.find(f => f.id === result.framework)?.name}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={copyContent} data-testid="copy-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? t("copied") : t("copy")}
              </Button>
              <Button size="sm" variant="outline" onClick={downloadContent} data-testid="download-btn">
                <Download className="w-4 h-4 mr-2" />{t("download")}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px] rounded border bg-muted/20 p-4">
              <pre className="text-sm whitespace-pre-wrap font-sans">{result.content}</pre>
            </ScrollArea>
          </CardContent>
        </Card>
      ) : null}

      {/* Fill Dialog */}
      <Dialog open={fillOpen} onOpenChange={setFillOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedTpl && (selectedTpl.title_es || selectedTpl?.title)}</DialogTitle>
            <DialogDescription>
              {t("pol.fill_dialog_desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("pol.org_name_label")}</Label>
                <Input value={instForm.org_name} onChange={e => setInstForm(f => ({ ...f, org_name: e.target.value }))} placeholder="ACME Corp" data-testid="inst-org-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("pol.address_label")}</Label>
                <Input value={instForm.org_address} onChange={e => setInstForm(f => ({ ...f, org_address: e.target.value }))} placeholder="..." />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("pol.version_label")}</Label>
                <Input value={instForm.version} onChange={e => setInstForm(f => ({ ...f, version: e.target.value }))} placeholder="1.0" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("pol.classification_label")}</Label>
                <Select value={instForm.classification} onValueChange={v => setInstForm(f => ({ ...f, classification: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Public">Public</SelectItem>
                    <SelectItem value="Internal">Internal</SelectItem>
                    <SelectItem value="Confidential">Confidential</SelectItem>
                    <SelectItem value="Restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("pol.effective_date_label")}</Label>
                <Input type="date" value={instForm.effective_date} onChange={e => setInstForm(f => ({ ...f, effective_date: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("pol.next_review_label")}</Label>
                <Input type="date" value={instForm.next_review_date} onChange={e => setInstForm(f => ({ ...f, next_review_date: e.target.value }))} />
              </div>
            </div>
            <div className="border-t pt-3 space-y-3">
              <h4 className="text-xs font-semibold">{t("pol.approver_signer")}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("name")} *</Label>
                  <Input value={instForm.approver_name} onChange={e => setInstForm(f => ({ ...f, approver_name: e.target.value }))} placeholder="John Doe" data-testid="inst-approver-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("pol.role_label")} *</Label>
                  <Input value={instForm.approver_role} onChange={e => setInstForm(f => ({ ...f, approver_role: e.target.value }))} placeholder="CEO" />
                </div>
              </div>
            </div>
            <div className="border-t pt-3 space-y-3">
              <h4 className="text-xs font-semibold">{t("pol.reviewer")}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("name")} *</Label>
                  <Input value={instForm.reviewer_name} onChange={e => setInstForm(f => ({ ...f, reviewer_name: e.target.value }))} placeholder="Jane Smith" data-testid="inst-reviewer-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("pol.role_label")} *</Label>
                  <Input value={instForm.reviewer_role} onChange={e => setInstForm(f => ({ ...f, reviewer_role: e.target.value }))} placeholder="CISO" />
                </div>
              </div>
            </div>
            <div className="space-y-1.5 pt-2">
              <Label className="text-xs">{t("pol.extra_context_label")}</Label>
              <Textarea rows={3} value={instForm.extra_context} onChange={e => setInstForm(f => ({ ...f, extra_context: e.target.value }))}
                placeholder={t("pol.extra_context_ph")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFillOpen(false)} disabled={instantiating}>{t("cancel")}</Button>
            <Button onClick={submitInstantiate} disabled={instantiating} data-testid="submit-instantiate">
              {instantiating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("generating")}</> : <><FileSignature className="w-4 h-4 mr-2" />{t("pol.generate_and_save")}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PolicyGenerator;
